import { v4 as uuidv4 } from "uuid";
import type { BaseBlock } from "@circle-react-shared/BuilderUI/DataModel.types";
import {
  getMarginOptions,
  getPaddingOptions,
} from "@circle-react-shared/BuilderUI/utils/boxModelStyleHelpers";
import {
  getBackgroundColorOptions,
  getBorderColorOptions,
  getBorderRadiusOptions,
  getBorderWidthOptions,
} from "@circle-react-shared/BuilderUI/utils/buttonStyleHelpers";
import {
  getHeightOptions,
  getMaxHeightOptions,
  getMaxWidthOptions,
  getMinHeightOptions,
  getMinWidthOptions,
  getWidthOptions,
} from "@circle-react-shared/BuilderUI/utils/sizeStyleHelpers";
import {
  getColorOptions,
  getFontSizeOptions,
  getFontStyleOptions,
  getFontWeightOptions,
  getLineHeightOptions,
  getTextAlignOptions,
  getTextDecorationOptions,
  getTextTransformOptions,
} from "@circle-react-shared/BuilderUI/utils/textStyleHelpers";
import { defaultPrimaryStyles } from "./defaultPrimaryStyles";
import { defaultSecondaryStyles } from "./defaultSecondaryStyles";

export const defaultData: BaseBlock = {
  id: uuidv4(),
  type: "button",
  props: {
    text: {
      prop: "other",
      type: "string",
      value: "Button Text",
    },
    href: {
      prop: "other",
      type: "string",
      value: "",
    },
    target: {
      prop: "other",
      type: "select",
      value: "_self",
    },
    buttonType: {
      prop: "other",
      type: "select",
      value: "primary",
    },
    styles: {
      prop: "styles",
      type: "styles",
      value: {
        primary: {
          default: {
            backgroundColor: getBackgroundColorOptions({
              defaultValue: defaultPrimaryStyles.default.backgroundColor,
            }),
            borderColor: getBorderColorOptions({
              defaultValue: defaultPrimaryStyles.default.borderColor,
            }),
            borderRadius: getBorderRadiusOptions({
              defaultValue: defaultPrimaryStyles.default.borderRadius,
            }),
            borderWidth: getBorderWidthOptions({
              defaultValue: defaultPrimaryStyles.default.borderWidth,
            }),
            color: getColorOptions(defaultPrimaryStyles.default.color),
            fontSize: getFontSizeOptions(defaultPrimaryStyles.default.fontSize),
            fontStyle: getFontStyleOptions(
              defaultPrimaryStyles.default.fontStyle,
            ),
            fontWeight: getFontWeightOptions(
              defaultPrimaryStyles.default.fontWeight,
            ),
            lineHeight: getLineHeightOptions(
              defaultPrimaryStyles.default.lineHeight,
            ),
            textAlign: getTextAlignOptions(
              defaultPrimaryStyles.default.textAlign,
            ),
            textDecoration: getTextDecorationOptions(
              defaultPrimaryStyles.default.textDecoration,
            ),
            textTransform: getTextTransformOptions(
              defaultPrimaryStyles.default.textTransform,
            ),
          },
          hover: {
            backgroundColor: getBackgroundColorOptions({
              defaultValue: defaultPrimaryStyles.hover.backgroundColor,
            }),
            borderColor: getBorderColorOptions({
              defaultValue: defaultPrimaryStyles.hover.borderColor,
            }),
            borderRadius: getBorderRadiusOptions({
              defaultValue: defaultPrimaryStyles.hover.borderRadius,
            }),
            borderWidth: getBorderWidthOptions({
              defaultValue: defaultPrimaryStyles.hover.borderWidth,
            }),
            color: getColorOptions(defaultPrimaryStyles.hover.color),
            fontSize: getFontSizeOptions(defaultPrimaryStyles.hover.fontSize),
            fontStyle: getFontStyleOptions(
              defaultPrimaryStyles.hover.fontStyle,
            ),
            fontWeight: getFontWeightOptions(
              defaultPrimaryStyles.hover.fontWeight,
            ),
            lineHeight: getLineHeightOptions(
              defaultPrimaryStyles.hover.lineHeight,
            ),
            textAlign: getTextAlignOptions(
              defaultPrimaryStyles.hover.textAlign,
            ),
            textDecoration: getTextDecorationOptions(
              defaultPrimaryStyles.hover.textDecoration,
            ),
            textTransform: getTextTransformOptions(
              defaultPrimaryStyles.hover.textTransform,
            ),
          },
          sizing: {
            width: getWidthOptions({
              defaultValue: defaultPrimaryStyles.sizing.width,
            }),
            height: getHeightOptions({
              defaultValue: defaultPrimaryStyles.sizing.height,
            }),
            minWidth: getMinWidthOptions({
              defaultValue: defaultPrimaryStyles.sizing.minWidth,
            }),
            minHeight: getMinHeightOptions({
              defaultValue: defaultPrimaryStyles.sizing.minHeight,
            }),
            maxWidth: getMaxWidthOptions({
              defaultValue: defaultPrimaryStyles.sizing.maxWidth,
            }),
            maxHeight: getMaxHeightOptions({
              defaultValue: defaultPrimaryStyles.sizing.maxHeight,
            }),
          },
          padding: getPaddingOptions({
            defaultValue: defaultPrimaryStyles.padding,
          }),
          margin: getMarginOptions({
            defaultValue: defaultPrimaryStyles.margin,
          }),
        },
        secondary: {
          default: {
            backgroundColor: getBackgroundColorOptions({
              defaultValue: defaultSecondaryStyles.default.backgroundColor,
            }),
            borderColor: getBorderColorOptions({
              defaultValue: defaultSecondaryStyles.default.borderColor,
            }),
            borderRadius: getBorderRadiusOptions({
              defaultValue: defaultSecondaryStyles.default.borderRadius,
            }),
            borderWidth: getBorderWidthOptions({
              defaultValue: defaultSecondaryStyles.default.borderWidth,
            }),
            color: getColorOptions(defaultSecondaryStyles.default.color),
            fontSize: getFontSizeOptions(
              defaultSecondaryStyles.default.fontSize,
            ),
            fontStyle: getFontStyleOptions(
              defaultSecondaryStyles.default.fontStyle,
            ),
            fontWeight: getFontWeightOptions(
              defaultSecondaryStyles.default.fontWeight,
            ),
            lineHeight: getLineHeightOptions(
              defaultSecondaryStyles.default.lineHeight,
            ),
            textAlign: getTextAlignOptions(
              defaultSecondaryStyles.default.textAlign,
            ),
            textDecoration: getTextDecorationOptions(
              defaultSecondaryStyles.default.textDecoration,
            ),
            textTransform: getTextTransformOptions(
              defaultSecondaryStyles.default.textTransform,
            ),
          },
          hover: {
            backgroundColor: getBackgroundColorOptions({
              defaultValue: defaultSecondaryStyles.hover.backgroundColor,
            }),
            borderColor: getBorderColorOptions({
              defaultValue: defaultSecondaryStyles.hover.borderColor,
            }),
            borderRadius: getBorderRadiusOptions({
              defaultValue: defaultSecondaryStyles.hover.borderRadius,
            }),
            borderWidth: getBorderWidthOptions({
              defaultValue: defaultSecondaryStyles.hover.borderWidth,
            }),
            color: getColorOptions(defaultSecondaryStyles.hover.color),
            fontSize: getFontSizeOptions(defaultSecondaryStyles.hover.fontSize),
            fontStyle: getFontStyleOptions(
              defaultSecondaryStyles.hover.fontStyle,
            ),
            fontWeight: getFontWeightOptions(
              defaultSecondaryStyles.hover.fontWeight,
            ),
            lineHeight: getLineHeightOptions(
              defaultSecondaryStyles.hover.lineHeight,
            ),
            textAlign: getTextAlignOptions(
              defaultSecondaryStyles.hover.textAlign,
            ),
            textDecoration: getTextDecorationOptions(
              defaultSecondaryStyles.hover.textDecoration,
            ),
            textTransform: getTextTransformOptions(
              defaultSecondaryStyles.hover.textTransform,
            ),
          },
          sizing: {
            width: getWidthOptions({
              defaultValue: defaultSecondaryStyles.sizing.width,
            }),
            height: getHeightOptions({
              defaultValue: defaultSecondaryStyles.sizing.height,
            }),
            minWidth: getMinWidthOptions({
              defaultValue: defaultSecondaryStyles.sizing.minWidth,
            }),
            minHeight: getMinHeightOptions({
              defaultValue: defaultSecondaryStyles.sizing.minHeight,
            }),
            maxWidth: getMaxWidthOptions({
              defaultValue: defaultSecondaryStyles.sizing.maxWidth,
            }),
            maxHeight: getMaxHeightOptions({
              defaultValue: defaultSecondaryStyles.sizing.maxHeight,
            }),
          },
          padding: getPaddingOptions({
            defaultValue: defaultSecondaryStyles.padding,
          }),
          margin: getMarginOptions({
            defaultValue: defaultSecondaryStyles.margin,
          }),
        },
        custom: {
          default: {
            backgroundColor: getBackgroundColorOptions({
              defaultValue: defaultSecondaryStyles.default.backgroundColor,
            }),
            borderColor: getBorderColorOptions({
              defaultValue: defaultSecondaryStyles.default.borderColor,
            }),
            borderRadius: getBorderRadiusOptions({
              defaultValue: defaultSecondaryStyles.default.borderRadius,
            }),
            borderWidth: getBorderWidthOptions({
              defaultValue: defaultSecondaryStyles.default.borderWidth,
            }),
            color: getColorOptions(defaultSecondaryStyles.default.color),
            fontSize: getFontSizeOptions(
              defaultSecondaryStyles.default.fontSize,
            ),
            fontStyle: getFontStyleOptions(
              defaultSecondaryStyles.default.fontStyle,
            ),
            fontWeight: getFontWeightOptions(
              defaultSecondaryStyles.default.fontWeight,
            ),
            lineHeight: getLineHeightOptions(
              defaultSecondaryStyles.default.lineHeight,
            ),
            textAlign: getTextAlignOptions(
              defaultSecondaryStyles.default.textAlign,
            ),
            textDecoration: getTextDecorationOptions(
              defaultSecondaryStyles.default.textDecoration,
            ),
            textTransform: getTextTransformOptions(
              defaultSecondaryStyles.default.textTransform,
            ),
          },
          hover: {
            backgroundColor: getBackgroundColorOptions({
              defaultValue: defaultSecondaryStyles.hover.backgroundColor,
            }),
            borderColor: getBorderColorOptions({
              defaultValue: defaultSecondaryStyles.hover.borderColor,
            }),
            borderRadius: getBorderRadiusOptions({
              defaultValue: defaultSecondaryStyles.hover.borderRadius,
            }),
            borderWidth: getBorderWidthOptions({
              defaultValue: defaultSecondaryStyles.hover.borderWidth,
            }),
            color: getColorOptions(defaultSecondaryStyles.hover.color),
            fontSize: getFontSizeOptions(defaultSecondaryStyles.hover.fontSize),
            fontStyle: getFontStyleOptions(
              defaultSecondaryStyles.hover.fontStyle,
            ),
            fontWeight: getFontWeightOptions(
              defaultSecondaryStyles.hover.fontWeight,
            ),
            lineHeight: getLineHeightOptions(
              defaultSecondaryStyles.hover.lineHeight,
            ),
            textAlign: getTextAlignOptions(
              defaultSecondaryStyles.hover.textAlign,
            ),
            textDecoration: getTextDecorationOptions(
              defaultSecondaryStyles.hover.textDecoration,
            ),
            textTransform: getTextTransformOptions(
              defaultSecondaryStyles.hover.textTransform,
            ),
          },
          sizing: {
            width: getWidthOptions({
              defaultValue: defaultSecondaryStyles.sizing.width,
            }),
            height: getHeightOptions({
              defaultValue: defaultSecondaryStyles.sizing.height,
            }),
            minWidth: getMinWidthOptions({
              defaultValue: defaultSecondaryStyles.sizing.minWidth,
            }),
            minHeight: getMinHeightOptions({
              defaultValue: defaultSecondaryStyles.sizing.minHeight,
            }),
            maxWidth: getMaxWidthOptions({
              defaultValue: defaultSecondaryStyles.sizing.maxWidth,
            }),
            maxHeight: getMaxHeightOptions({
              defaultValue: defaultSecondaryStyles.sizing.maxHeight,
            }),
          },
          padding: getPaddingOptions({
            defaultValue: defaultSecondaryStyles.padding,
          }),
          margin: getMarginOptions({
            defaultValue: defaultSecondaryStyles.margin,
          }),
        },
      },
    },
  },
};
